import { useCallback, useEffect, useId, useRef, useState } from 'react';

export type BomCartAddPayload = {
	partId: string | number;
	qty: number;
	partNumber?: string;
	partName?: string;
};

export function defaultQtyFromBomQuantity(q: string | number | null | undefined): number {
	if (q === undefined || q === null) {
		return 1;
	}
	if (typeof q === 'number' && Number.isFinite(q) && q > 0) {
		return Math.max(1, Math.floor(q));
	}
	const s = String(q).trim();
	const lead = s.match(/^\s*(\d+)/);
	if (lead) {
		return Math.max(1, parseInt(lead[1], 10));
	}
	const n = Number(s);
	if (Number.isFinite(n) && n > 0) {
		return Math.max(1, Math.floor(n));
	}
	return 1;
}

function CartPlusIcon() {
	return (
		<svg
			className="bom-cart-icon"
			width={18}
			height={18}
			viewBox="0 0 24 24"
			fill="none"
			stroke="currentColor"
			strokeWidth={2}
			strokeLinecap="round"
			strokeLinejoin="round"
			aria-hidden
		>
			<circle cx={9} cy={20} r={1} />
			<circle cx={18} cy={20} r={1} />
			<path d="M1 1h3l1.5 9h13L21 6H5.5" />
			<path d="M16 11h6M19 8v6" />
		</svg>
	);
}

type Props = {
	partId: string | number;
	bomQuantity: string | number | null | undefined;
	partNumber?: string;
	partName?: string;
	onAdd: (payload: BomCartAddPayload) => void;
};

export function BomAddToCartCell({ partId, bomQuantity, partNumber, partName, onAdd }: Props) {
	const dialogId = useId();
	const triggerRef = useRef<HTMLButtonElement>(null);
	const panelRef = useRef<HTMLDivElement>(null);
	const cellRef = useRef<HTMLDivElement>(null);
	const [open, setOpen] = useState(false);
	const [qtyStr, setQtyStr] = useState('1');

	const close = useCallback(() => {
		setOpen(false);
	}, []);

	const openPanel = useCallback(() => {
		setQtyStr(String(defaultQtyFromBomQuantity(bomQuantity)));
		setOpen(true);
	}, [bomQuantity]);

	useEffect(() => {
		if (!open) {
			return;
		}
		const onDocDown = (e: MouseEvent) => {
			const t = e.target as Node;
			if (panelRef.current?.contains(t) || triggerRef.current?.contains(t) || cellRef.current?.contains(t)) {
				return;
			}
			close();
		};
		const onKey = (e: KeyboardEvent) => {
			if (e.key === 'Escape') {
				close();
			}
		};
		document.addEventListener('mousedown', onDocDown);
		document.addEventListener('keydown', onKey);
		return () => {
			document.removeEventListener('mousedown', onDocDown);
			document.removeEventListener('keydown', onKey);
		};
	}, [open, close]);

	const handleAdd = () => {
		const raw = Number(String(qtyStr).replace(/,/g, ''));
		const n = Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
		onAdd({
			partId,
			qty: n,
			partNumber,
			partName,
		});
		close();
	};

	return (
		<div ref={cellRef} className="bom-cart-cell">
			<button
				ref={triggerRef}
				type="button"
				className="bom-cart-trigger"
				aria-expanded={open}
				aria-haspopup="dialog"
				aria-controls={open ? dialogId : undefined}
				title="Add to shopping cart"
				onClick={() => {
					if (open) {
						close();
					} else {
						openPanel();
					}
				}}
			>
				<CartPlusIcon />
			</button>
			{open ? (
				<div
					ref={panelRef}
					id={dialogId}
					className="bom-cart-popover"
					role="dialog"
					aria-modal="true"
					aria-labelledby={`${dialogId}-title`}
					style={{
						position: 'absolute',
						top: '100%',
						right: 0,
						marginTop: 6,
						width: 220,
						zIndex: 50,
					}}
				>
					<div id={`${dialogId}-title`} className="bom-cart-popover-title">
						Add to cart
					</div>
					<label className="bom-cart-popover-label">
						<span>Quantity</span>
						<input type="number" className="bom-cart-popover-input" min={1} step={1} value={qtyStr} onChange={(e) => setQtyStr(e.target.value)} />
					</label>
					<div className="bom-cart-popover-actions">
						<button type="button" className="bom-cart-popover-btn bom-cart-popover-btn--primary" onClick={handleAdd}>
							Add
						</button>
						<button type="button" className="bom-cart-popover-btn bom-cart-popover-btn--ghost" onClick={close}>
							Cancel
						</button>
					</div>
				</div>
			) : null}
		</div>
	);
}
